import SearchBar from '-/Custom/SearchBar';
import { Stack } from 'expo-router';
import { useState } from 'react';
import * as PersonListItem from '@/components/Person/PersonListItem';
import Box from '@/components/UI/Box';
import Item from '@/components/UI/PressableRow';
import List from '@/components/Unistyles/List';
import { graphql, readFragment } from '@/gql/graphql';
import type { PersonBoolExp } from '@/gql/scalars';
import useSearchablePaginatedQuery from '@/hooks/useSearchablePaginatedQuery';
import { DEFAULT_SEARCH_MINIMUM } from '@/utils/constants';

const GetPeople = graphql(/* GraphQL */ `
  query GetOwnershipTransferPeople(
    $where: person_bool_exp!
    $limit: Int!
    $offset: Int!
  ) {
    person(
      where: $where
      limit: $limit
      offset: $offset
      order_by: { name: asc }
    ) {
      id
      ...PersonListItemAvatar
      ...PersonListItemName
    }
  }
`, [
  PersonListItem.PersonListItemAvatarFragment,
  PersonListItem.PersonListItemNameFragment,
]);

const SearchPeople = graphql(/* GraphQL */ `
  query SearchOwnershipTransferPeople(
    $where: person_bool_exp!
    $limit: Int!
    $offset: Int!
  ) {
    person(
      where: $where
      limit: $limit
      offset: $offset
      order_by: { name: asc }
    ) {
      id
      ...PersonListItemAvatar
      ...PersonListItemName
    }
  }
`, [
  PersonListItem.PersonListItemAvatarFragment,
  PersonListItem.PersonListItemNameFragment,
]);

export default function OwnershipTransferPeople({
  title,
  where,
  onSelect,
}: {
  title: string;
  where: PersonBoolExp;
  onSelect: (person: { id: string; name: string | null }) => void;
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const baseWhere = where;

  const searchWhere: PersonBoolExp = {
    _and: [
      baseWhere,
      {
        name: { _ilike: `%${searchTerm}%` },
      },
    ],
  };

  const { items, fetching } = useSearchablePaginatedQuery({
    searchTerm,
    searchOnMinimum: DEFAULT_SEARCH_MINIMUM,
    pageSize: 25,
    getQuery: GetPeople,
    getVariables: ({ limit, offset }) => ({
      where: baseWhere,
      limit,
      offset,
    }),
    selectGetItems: (data) => data?.person ?? [],
    searchQuery: SearchPeople,
    searchVariables: ({ searchTerm: term }) => ({
      where: term.length >= DEFAULT_SEARCH_MINIMUM ? searchWhere : baseWhere,
      limit: 25,
      offset: 0,
    }),
    selectSearchItems: (data) => data?.person ?? [],
  });

  return (
    <>
      <Stack.Screen options={{ title: `Transfer ${title}` }} />
      <Box p="md">
        <SearchBar onChangeText={setSearchTerm} />
      </Box>
      <List
        data={items}
        fetching={fetching}
        renderItem={({ item }) => {
          const person = readFragment(
            PersonListItem.PersonListItemNameFragment,
            item,
          );

          return (
            <Item
              aria-label={person.name || 'Unknown User'}
              onPress={() => {
                onSelect({ id: item.id, name: person.name });
              }}
            >
              <PersonListItem.Container>
                <PersonListItem.Avatar fragment={item} />
                <PersonListItem.Content>
                  <PersonListItem.Name fragment={item} />
                </PersonListItem.Content>
              </PersonListItem.Container>
            </Item>
          );
        }}
      />
    </>
  );
}
